import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../backlog/backlog.css';
import './suboutRegistration.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { useHotkeys } from '../../../components/hotkeys';
import { FormReportsMenu } from '../../../components/FormReportsMenu';
import { ProjectNotesButton } from '../../../components/ProjectNotes';
import { guardedExit, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { UsDateInput } from '../../../components/UsDateInput';
import { useAuthStore } from '../../../auth/store';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import {
  apiMessage, computeTestPrices, fetchDueDate, fetchSampleDetails, fetchTestOrderSamples, fetchTestPrices,
  registerSubout, scanContainer, updateTestOrder,
} from './api';
import PreLoginDocsPanel from '../../settings/preLoginSettings/PreLoginDocsPanel';
import { suboutDocsOwner } from '../suboutDocs';
import SuboutAddSamplesDialog from './SuboutAddSamplesDialog';
import SuboutTestPricesTab from './SuboutTestPricesTab';
import type {
  SuboutLabItem, SuboutLookups, SuboutRegistrationKey, SuboutRegistrationRow, SuboutSampleDetailRow,
  SuboutTestOrderDetail, SuboutTestPriceOp, SuboutTestPriceRow,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Subout Registration (New) / Subout Test Order (Edit) — full-page detail view,
// matching legacy ALPACA SubOutSampleRegistrations_DetailView_Copy / _TestOrder. The
// list is REPLACED by this page (standing rule 12). Sections: Subout Info · Contract
// Lab Info · Shipping Info · Test Info, then the tabs.
//
// G5 parity (2026-10-26):
//  • Due Date is read-only and computed by the server from the TAT (TAT hours from the
//    order's received date, working days / business hours, holidays skipped).
//  • Edit mode: Subout Samples → Add / Remove (staged, applied on Save) and the Test
//    Price tab (TAT / Unit Price / Comment editable; every change recomputed by the
//    server). The bottle count follows the samples, as in G5.
//  • Reports ▾ = the lab's Form Report Permission menu for this view; Project Notes =
//    the shared popup over the order's SuboutOrderID (and its jobs). Both need a saved
//    order, so they are disabled while registering.
//
// G5 task 30247 (changesets 1202/1203, 2026-11-02) — New mode only:
//  • Scan Code (Test Info): scan a container label JobID-SampleNo-Bottle; the API
//    matches it against the rows on the screen and every Subout row carrying that
//    bottle is ticked. Enter or leaving the box scans; the box clears for the next one.
//    The ticks are a visual check only — Register still registers every row, as in G5.
//  • BottleID column (after Parameter): the test's bottles; a multi-bottle row shows a
//    dropdown whose pick (or the last scanned letter) is display-only, never saved.
// ─────────────────────────────────────────────────────────────────────────────

const TEST_ORDER_DOCS = suboutDocsOwner('test-order');

interface Props {
  samples?: SuboutRegistrationRow[];  // NEW mode: the selected PENDING rows being registered
  edit?: { order: SuboutTestOrderDetail; samples: SuboutSampleDetailRow[] };  // EDIT mode (Subout Test Order)
  lookups: SuboutLookups | null;
  canCreate: boolean;                 // create (new) or write (edit) right
  onClose: () => void;                // Cancel / Delete → back to list
  onSaved: () => void;                // after a successful save → refresh + list
}

const EMPTY_LAB: SuboutLabItem = {
  id: '', name: null, accreditationId: null, contact: null, phone: null, email: null, address: null,
};

const keyOf = (r: { sampleOid: string; testMethodOid: string }) => `${r.sampleOid}:${r.testMethodOid}`;
const toKey = (r: SuboutSampleDetailRow): SuboutRegistrationKey => ({ sampleOid: r.sampleOid, testMethodOid: r.testMethodOid });

const SAMPLE_COLUMNS: AppColumn<SuboutSampleDetailRow>[] = [
  { field: 'jobId',           header: 'JobID',          width: 110, text: (r) => r.jobId ?? '' },
  { field: 'sampleId',        header: 'SampleID',       width: 130, text: (r) => r.sampleId ?? '' },
  { field: 'matrix',          header: 'Matrix',         width: 110, text: (r) => r.matrix ?? '' },
  { field: 'test',            header: 'Test',           width: 150, text: (r) => r.test ?? '' },
  { field: 'method',          header: 'Method',         width: 120, text: (r) => r.method ?? '' },
  { field: 'parameter',       header: 'Parameter',      width: 180, text: (r) => r.parameter ?? '' },
  { field: 'containerType',   header: 'ContainerType',  width: 140, text: (r) => r.containerType ?? '' },
  { field: 'holdTime',        header: 'HoldTime',       width: 100, text: (r) => r.holdTime ?? '' },
  { field: 'dateCollected',   header: 'DateCollected',  width: 120, type: 'date', text: (r) => fmtLocalDate(r.dateCollected) },
  { field: 'originalDueDate', header: 'OriginalDueDate', width: 130, type: 'date', text: (r) => fmtLocalDate(r.originalDueDate) },
  { field: 'client',          header: 'Client',         width: 200, text: (r) => r.client ?? '' },
];

/** The registration grid's BottleID column (G5 NPBottleIDs). A multi-bottle row shows a
 *  dropdown whose pick is display-only; the scanned letter is pushed into it. */
function bottleColumn(
  picks: Record<string, string>, onPick: (key: string, value: string) => void,
): AppColumn<SuboutSampleDetailRow> {
  return {
    field: 'bottleIds', header: 'BottleID', width: 100,
    text: (r) => picks[keyOf(r)] ?? r.bottleIds ?? '',
    render: (r) => {
      const all = r.bottleIds ?? '';
      const list = all.split(',').map((b) => b.trim()).filter(Boolean);
      if (list.length < 2) return all;
      const key = keyOf(r);
      return (
        <select className="srd-bottle" data-unsaved-ignore aria-label={`Bottle for ${r.sampleId ?? ''} ${r.test ?? ''}`}
                value={picks[key] ?? all}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={(e) => onPick(key, e.target.value)}>
          <option value={all}>{all}</option>
          {list.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      );
    },
  };
}

type Tab = 'samples' | 'prices' | 'attachments' | 'notes';

export default function SuboutRegistrationDetail({ samples = [], edit, lookups, canCreate, onClose, onSaved }: Props) {
  const notify = useNotify();
  const confirm = useConfirm();
  const userName = useAuthStore((s) => s.user?.name) ?? 'Service';
  const o = edit?.order;
  const isEdit = !!edit;

  // ── Editable fields (pre-filled from the order in Edit mode) ────────────────
  const [labOid, setLabOid]   = useState(o?.contractLabOid ?? '');
  const [tatOid, setTatOid]   = useState(o?.turnAroundTimeOid ?? '');
  const [bottles, setBottles] = useState(o ? String(o.numberOfBottles ?? '') : String(new Set(samples.map((s) => s.sampleId)).size || 1));
  const [preservativeOid, setPreservativeOid] = useState(o?.preservativeOid ?? '');
  const [deliveryOid, setDeliveryOid] = useState(o?.deliveryServiceOid ?? '');
  const [trackingNo, setTrackingNo]   = useState(o?.trackingNo ?? '');
  const [remark, setRemark]   = useState(o?.remark ?? '');
  const [dateShipped, setDateShipped] = useState(o?.dateShipped ? o.dateShipped.slice(0, 10) : '');
  const [dueDate, setDueDate]         = useState<string | null>(o?.dueDate ?? null);
  const [shipContact, setShipContact] = useState(o?.deliveryContact ?? '');
  const [shipPhone, setShipPhone]     = useState(o?.deliveryContactPhone ?? '');
  const [shipEmail, setShipEmail]     = useState(o?.deliveryEmail ?? '');

  // Contract Lab Info — editable + REQUIRED (ALPACA parity), auto-filled from the
  // lab when one is picked, then overridable.
  const [labContact, setLabContact] = useState(o?.contact ?? '');
  const [labPhone, setLabPhone]     = useState(o?.contactPhone ?? '');
  const [labAddr, setLabAddr]       = useState(o?.labAddress ?? '');

  const [tab, setTab]   = useState<Tab>('samples');
  const [busy, setBusy] = useState(false);
  // Attachments / Notes / Project Notes / Reports belong to a saved order.
  const orderOid = o?.id ?? null;
  const [docCounts, setDocCounts] = useState({ attachments: 0, notes: 0 });

  // Accreditation ID / Contact Email are read-only, derived from the lab.
  const lab = useMemo<SuboutLabItem>(
    () => lookups?.contractLabs.find((l) => l.id === labOid) ?? EMPTY_LAB,
    [lookups, labOid]);

  // ── Subout Samples grid ────────────────────────────────────────────────────
  const [sampleRows, setSampleRows] = useState<SuboutSampleDetailRow[]>(edit?.samples ?? []);
  const [added, setAdded]     = useState<SuboutSampleDetailRow[]>([]);
  const [removed, setRemoved] = useState<SuboutSampleDetailRow[]>([]);
  const [sampleSel, setSampleSel] = useState<Set<string | number>>(new Set());
  const [gridLoading, setGridLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  // ── Scan Code + BottleID (New mode only — G5 task 30247) ───────────────────
  const [scanText, setScanText] = useState('');
  const [bottlePicks, setBottlePicks] = useState<Record<string, string>>({});
  const scanInFlight = useRef(false);
  const sampleColumns = useMemo<AppColumn<SuboutSampleDetailRow>[]>(() => {
    if (isEdit) return SAMPLE_COLUMNS;
    const at = SAMPLE_COLUMNS.findIndex((c) => c.field === 'parameter') + 1;
    const col = bottleColumn(bottlePicks, (key, value) => setBottlePicks((p) => {
      const next = { ...p };
      if (value.includes(',')) delete next[key]; else next[key] = value;
      return next;
    }));
    return [...SAMPLE_COLUMNS.slice(0, at), col, ...SAMPLE_COLUMNS.slice(at)];
  }, [isEdit, bottlePicks]);

  // ── Test Price tab (Edit only) ─────────────────────────────────────────────
  const [prices, setPrices] = useState<SuboutTestPriceRow[]>([]);
  const [priceTotal, setPriceTotal] = useState(0);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [pricesDirty, setPricesDirty] = useState(false);

  // Staged edits the shared tracker cannot see (standing rule 15).
  useUnsavedGuard(isEdit && (added.length > 0 || removed.length > 0 || pricesDirty));

  // ── Test Info ──────────────────────────────────────────────────────────────
  const srcSamples = useMemo<{ test: string | null; sampleId: string | null }[]>(
    () => (isEdit ? sampleRows : samples), [isEdit, sampleRows, samples]);
  const testForSubout = useMemo(
    () => [...new Set(srcSamples.map((s) => s.test).filter(Boolean))].join(', '), [srcSamples]);
  const numberOfSamples = useMemo(() => new Set(srcSamples.map((s) => s.sampleId)).size, [srcSamples]);

  const loadSamples = useCallback(async () => {
    setGridLoading(true);
    try {
      if (isEdit) {
        setSampleRows(await fetchTestOrderSamples(orderOid!));
        setAdded([]); setRemoved([]); setSampleSel(new Set());
      } else {
        setSampleRows(await fetchSampleDetails(
          samples.map((s) => ({ sampleOid: s.sampleOid, testMethodOid: s.testMethodOid }))));
        setSampleSel(new Set()); setBottlePicks({});
      }
    } catch (e) {
      notify.error(apiMessage(e, 'Failed to load the subout samples.'));
    } finally {
      setGridLoading(false);
    }
  }, [isEdit, orderOid, samples, notify]);

  const loadPrices = useCallback(async () => {
    if (!isEdit || !orderOid) return;
    setPricesLoading(true);
    try {
      const res = await fetchTestPrices(orderOid);
      setPrices(res.rows); setPriceTotal(res.total); setPricesDirty(false);
    } catch (e) {
      notify.error(apiMessage(e, 'Failed to load the test prices.'));
    } finally {
      setPricesLoading(false);
    }
  }, [isEdit, orderOid, notify]);

  useEffect(() => { if (!isEdit) void loadSamples(); }, [isEdit, loadSamples]);
  useEffect(() => { void loadPrices(); }, [loadPrices]);

  // Server-side price rules after one change (G5 recomputes on every edit).
  async function recompute(
    op: SuboutTestPriceOp,
    opts: { rows?: SuboutTestPriceRow[]; samples?: SuboutSampleDetailRow[]; lab?: string;
            testMethodOid?: string; tatOid?: string | null; unitPrice?: number } = {},
  ) {
    const contractLab = opts.lab ?? labOid;
    if (!isEdit || !contractLab) return;
    try {
      const res = await computeTestPrices({
        op, contractLabOid: contractLab, turnAroundTimeOid: tatOid || null,
        samples: (opts.samples ?? sampleRows).map(toKey),
        rows: opts.rows ?? prices,
        testMethodOid: opts.testMethodOid ?? null,
        tatOid: opts.tatOid ?? null,
        unitPrice: opts.unitPrice ?? null,
      });
      setPrices(res.rows); setPriceTotal(res.total); setPricesDirty(true);
      if (res.bottles != null) setBottles(String(res.bottles));   // G5 calculateBottles
    } catch (e) {
      notify.warning(apiMessage(e, 'The test prices could not be recalculated.'));
    }
  }

  // ── Due Date (read-only, server-computed — G5 ObjectChanged :2729) ──────────
  useEffect(() => {
    if (isEdit && tatOid === (o?.turnAroundTimeOid ?? '')) { setDueDate(o?.dueDate ?? null); return; }
    if (!tatOid) { setDueDate(null); return; }
    let live = true;
    fetchDueDate(tatOid, isEdit ? o?.dateReceived : null)
      .then((d) => { if (live) setDueDate(d); })
      .catch(() => { if (live) setDueDate(null); });
    return () => { live = false; };
  }, [tatOid, isEdit, o?.turnAroundTimeOid, o?.dueDate, o?.dateReceived]);

  // Pick a lab → fill the editable Contact Phone / Contact / Lab Address; in Edit mode the
  // Test Price rows are re-matched to the new lab (G5 ContractLabName change :2930).
  function pickLab(id: string) {
    setLabOid(id);
    const l = lookups?.contractLabs.find((x) => x.id === id);
    setLabContact(l?.contact ?? '');
    setLabPhone(l?.phone ?? '');
    setLabAddr(l?.address ?? '');
    if (isEdit && id) void recompute('lab', { lab: id });
  }

  // ── Subout Samples → Add / Remove (G5 :9534-9858, staged until Save) ────────
  function addSamples(rows: SuboutSampleDetailRow[]) {
    const removedKeys = new Set(removed.map(keyOf));
    const backFromRemoved = rows.filter((r) => removedKeys.has(keyOf(r)));
    const brandNew = rows.filter((r) => !removedKeys.has(keyOf(r)));
    const nextRemoved = removed.filter((r) => !backFromRemoved.some((b) => keyOf(b) === keyOf(r)));
    const nextRows = [...sampleRows, ...rows];
    setRemoved(nextRemoved);
    setAdded([...added, ...brandNew]);
    setSampleRows(nextRows);
    setShowAdd(false);
    void recompute('sync', { samples: nextRows });
  }

  function removeSamples() {
    const picked = sampleRows.filter((r) => sampleSel.has(keyOf(r)));
    // G5 messages, in G5's order (SuboutSampleRegistrationViewController :9534-9634).
    if (picked.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    if (picked.length === sampleRows.length) { notify.warning('Atleast one test must still need subout samples.'); return; }
    if (picked.some((r) => (r.maxStatus ?? 0) !== 0)) {
      notify.warning('Unable to remove the item since it has been referenced already.');
      return;
    }
    const addedKeys = new Set(added.map(keyOf));
    const pickedKeys = new Set(picked.map(keyOf));
    setAdded(added.filter((r) => !pickedKeys.has(keyOf(r))));
    setRemoved([...removed, ...picked.filter((r) => !addedKeys.has(keyOf(r)))]);
    const nextRows = sampleRows.filter((r) => !pickedKeys.has(keyOf(r)));
    setSampleRows(nextRows);
    setSampleSel(new Set());
    notify.success(picked.length === 1 ? 'The selected item was removed.' : 'The selected items were removed.');
    void recompute('sync', { samples: nextRows });
  }

  function editPrice(row: SuboutTestPriceRow, field: 'tat' | 'price' | 'comment', value: string) {
    if (field === 'comment') {
      setPrices(prices.map((p) => (p.testMethodOid === row.testMethodOid ? { ...p, comment: value } : p)));
      setPricesDirty(true);
      return;
    }
    if (field === 'tat') {
      void recompute('tat', { testMethodOid: row.testMethodOid, tatOid: value || null });
      return;
    }
    const n = Number(value);
    if (Number.isNaN(n)) return;
    void recompute('price', { testMethodOid: row.testMethodOid, unitPrice: n });
  }

  // ── Scan Code (G5 SubOutScanBottle): the API checks the label against the rows on
  // the screen; matching Subout rows are ticked and a multi-bottle row shows the
  // scanned letter. The box is cleared at once so a blur after Enter cannot re-scan.
  async function scan(raw: string) {
    const label = raw.trim();
    setScanText('');
    if (!label || scanInFlight.current) return;
    scanInFlight.current = true;
    try {
      const res = await scanContainer(label, sampleRows.map(toKey));
      const picked = new Set(res.matched.map(keyOf));
      setSampleSel((prev) => new Set([...prev, ...picked]));
      setBottlePicks((prev) => {
        const next = { ...prev };
        for (const r of sampleRows) {
          if (picked.has(keyOf(r)) && (r.bottleIds ?? '').includes(',') && res.bottle) next[keyOf(r)] = res.bottle;
        }
        return next;
      });
    } catch (e) {
      notify.error(apiMessage(e, 'The container label could not be checked.'));
    } finally {
      scanInFlight.current = false;
    }
  }

  // ── Save (register in New mode, update in Edit mode) ────────────────────────
  async function save(close: boolean) {
    if (busy) return;
    if (!canCreate) { notify.warning(`You do not have permission to ${isEdit ? 'edit this subout order' : 'register subout samples'}.`); return; }
    // Required fields (ALPACA parity): Contract Lab, Contact Phone, Contact,
    // Lab Address, Turn Around Time, Tracking#. G5 makes Tracking# required on every
    // save, registration included (BO TrackingNo [RuleRequiredField], 2026-10-24).
    const missing: string[] = [];
    if (!labOid)            missing.push('Contract Lab');
    if (!labPhone.trim())   missing.push('Contact Phone');
    if (!labContact.trim()) missing.push('Contact');
    if (!labAddr.trim())    missing.push('Lab Address');
    if (!tatOid)            missing.push('Turn Around Time');
    if (!trackingNo.trim()) missing.push('Tracking#');
    if (missing.length) { notify.warning(`Please fill the required field(s): ${missing.join(', ')}.`); return; }

    const ok = await confirm.confirm({
      title: 'Do you want to Save?',
      message: isEdit
        ? `Save changes to subout order ${o?.suboutOrderId}?`
        : `Register ${numberOfSamples} sample${numberOfSamples === 1 ? '' : 's'} (${samples.length} test${samples.length === 1 ? '' : 's'}) to a new subout order?`,
    });
    if (!ok) return;

    setBusy(true);
    try {
      if (isEdit) {
        const updated = await updateTestOrder(o!.id!, {
          contractLabOid: labOid,
          turnAroundTimeOid: tatOid || null,
          numberOfBottles: bottles ? Number(bottles) : null,
          preservativeOid: preservativeOid || null,
          deliveryServiceOid: deliveryOid || null,
          trackingNo: trackingNo.trim() || null,
          remark: remark.trim() || null,
          dateShipped: dateShipped || null,
          labAddress: labAddr.trim() || null,
          contact: labContact.trim() || null,
          contactPhone: labPhone.trim() || null,
          deliveryContact: shipContact.trim() || null,
          deliveryContactPhone: shipPhone.trim() || null,
          deliveryEmail: shipEmail.trim() || null,
          addSamples: added.map(toKey),
          removeSamples: removed.map(toKey),
          testPrices: prices,
        });
        setAdded([]); setRemoved([]); setPricesDirty(false);
        notify.success(`Subout order ${updated.suboutOrderId} saved successfully.`);
        onSaved();
      } else {
        const res = await registerSubout({
          items: samples.map((s) => ({ sampleOid: s.sampleOid, testMethodOid: s.testMethodOid })),
          contractLabOid: labOid,
          turnAroundTimeOid: tatOid || null,
          numberOfBottles: bottles ? Number(bottles) : null,
          preservativeOid: preservativeOid || null,
          deliveryServiceOid: deliveryOid || null,
          trackingNo: trackingNo.trim() || null,
          remark: remark.trim() || null,
          dateShipped: dateShipped || null,
          dueDate: null,   // the server computes it from the TAT
          labAddress: labAddr.trim() || null,
          contact: labContact.trim() || null,
          contactPhone: labPhone.trim() || null,
          deliveryContact: shipContact.trim() || null,
          deliveryContactPhone: shipPhone.trim() || null,
          deliveryEmail: shipEmail.trim() || null,
        });
        // G5 wording (SuboutSampleRegistrationViewController :1599 / :1918).
        notify.success(`The subout ID '${res.orderId}' was saved successfully.`);
        onSaved();
      }
      void close;   // new-only distinction not needed; both finish to the list
    } catch (e) {
      notify.error(apiMessage(e, isEdit ? 'Failed to save the order.' : 'The registration failed.'));
    } finally {
      setBusy(false);
    }
  }

  const opt = (items?: { id: string; name: string | null }[]) =>
    (items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name ?? '—'}</option>);

  async function refresh() {
    await loadSamples();
    await loadPrices();
  }

  // Leaving with unsaved edits asks first (standing rule 15). The shared tracker
  // watches this form's native inputs, so Cancel / Delete-discard / Esc all go
  // through the ONE "Unsaved changes will be lost" confirm.
  const leave = () => void guardedExit(onClose);
  const formTitle = isEdit ? 'Subcontract Test Order' : 'Subcontract Registration';
  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: formTitle, order: 1, disabled: busy, run: () => void save(false) },
    { combo: 'mod+enter', label: 'Save and Close', group: formTitle, order: 2, disabled: busy, run: () => void save(true) },
    { combo: 'alt+r', label: 'Refresh', group: formTitle, order: 3, disabled: busy || gridLoading, run: () => void refresh() },
    { combo: 'esc', label: 'Cancel', group: formTitle, order: 9, allowInInput: false, disabled: busy, run: leave },
  ]);

  const reportView = isEdit ? 'test-order' : 'registration';
  const actionBar = (
    <div className="srd-actions">
      <div className="srd-actions-left">
        <FormReportsMenu
          reportsUrl={`/api/v1/subout-reports?view=${reportView}`}
          pdfUrl={`/api/v1/subout-reports/pdf?view=${reportView}`}
          keysParam="orders"
          selectedKeys={o?.suboutOrderId ? [o.suboutOrderId] : []}
        />
        <button type="button" className="ca-btn" onClick={leave} disabled={busy}>
          <i className="ti ti-trash" aria-hidden="true" /> Delete
        </button>
        <button type="button" className="ca-btn" onClick={() => void refresh()} disabled={busy || gridLoading}
                title="Refresh (Alt+R)">
          <i className="ti ti-refresh" aria-hidden="true" /> Refresh
        </button>
        <ProjectNotesButton
          recordId={orderOid}
          baseUrl={orderOid ? `/api/v1/subout-orders/test-order/${orderOid}/project-notes` : null}
          recordLabel={o?.suboutOrderId ?? ''}
          businessObject={isEdit ? 'Subout Test Order' : 'Subout Sample Registration'}
          readOnly={!canCreate}
          disabledReason="Save the subout order before adding Project Notes"
        />
      </div>
      <div className="srd-actions-right">
        <button type="button" className="ca-btn ca-btn-solid" onClick={() => void save(false)} disabled={busy}>
          <i className="ti ti-device-floppy" aria-hidden="true" /> Save
        </button>
        <button type="button" className="ca-btn ca-btn-solid" onClick={() => void save(true)} disabled={busy}>
          <i className="ti ti-check" aria-hidden="true" /> Save and Close
        </button>
        <button type="button" className="ca-btn" onClick={leave} disabled={busy} title="Cancel (Esc)">
          <i className="ti ti-x" aria-hidden="true" /> Cancel
        </button>
      </div>
    </div>
  );

  const tabs: Tab[] = isEdit ? ['samples', 'prices', 'attachments', 'notes'] : ['samples', 'attachments', 'notes'];
  const tabLabel = (t: Tab) =>
    t === 'samples' ? `Subout Samples (${numberOfSamples})`
      : t === 'prices' ? `Test Price (${prices.length})`
      : t === 'attachments' ? `Attachments (${docCounts.attachments})` : `Notes (${docCounts.notes})`;

  return (
    <div className="bl-page srd-page">
      {/* Breadcrumb + title */}
      <div className="srd-head">
        <div className="dp-breadcrumb">
          <span>Sample SubContract Tracking</span><i className="ti ti-chevron-right" />
          <span>{isEdit ? 'Subcontract Test Order' : 'Subcontract Registration'}</span><i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{isEdit ? (o?.suboutOrderId ?? 'Edit') : 'New'}</span>
        </div>
        <h2 className="srd-title">{isEdit ? 'Subcontract Test Order' : 'Subcontract Registration'}</h2>
      </div>

      {actionBar}

      <div className="srd-body">
        {/* ── Subout Info ── */}
        <section className="srd-sec">
          <h3>Subout Info</h3>
          <div className="srd-grid">
            <label>Subout Order ID</label>
            <input value={isEdit ? (o?.suboutOrderId ?? '') : 'AutoGenerate'} readOnly className="srd-ro" />
            <label>Registered By</label>
            <input value={isEdit ? (o?.registeredBy ?? '') : userName} readOnly className="srd-ro" />
            <label>Date Registered</label>
            <input value={fmtLocalDate(isEdit ? o?.dateRegistered : new Date().toISOString())} readOnly className="srd-ro" />
            {isEdit ? <><label>Status</label><input value={o?.statusText ?? ''} readOnly className="srd-ro" /></> : <><span /><span /></>}
          </div>
        </section>

        {/* ── Contract Lab Info ── */}
        <section className="srd-sec">
          <h3>Contract Lab Info</h3>
          <div className="srd-grid">
            <label>Contract Lab<span className="srd-req">*</span></label>
            <select className={labOid ? '' : 'srd-empty'} value={labOid} onChange={(e) => pickLab(e.target.value)}>
              <option value="">— select —</option>
              {opt(lookups?.contractLabs)}
            </select>
            <label>Contact Phone<span className="srd-req">*</span></label>
            <input className={labPhone.trim() ? '' : 'srd-empty'} value={labPhone} maxLength={100}
                   onChange={(e) => setLabPhone(e.target.value)} />

            <label>Accreditation ID</label>
            <input value={lab.accreditationId ?? ''} readOnly className="srd-ro" />
            <label>Contact Email</label>
            <input value={lab.email ?? ''} readOnly className="srd-ro" />

            <label>Contact<span className="srd-req">*</span></label>
            <input className={labContact.trim() ? '' : 'srd-empty'} value={labContact} maxLength={100}
                   onChange={(e) => setLabContact(e.target.value)} />
            <label>Lab Address<span className="srd-req">*</span></label>
            <input className={labAddr.trim() ? '' : 'srd-empty'} value={labAddr} maxLength={100}
                   onChange={(e) => setLabAddr(e.target.value)} />
          </div>
        </section>

        {/* ── Shipping Info ── */}
        <section className="srd-sec">
          <h3>Shipping Info</h3>
          <div className="srd-grid">
            <label>Delivery Service</label>
            <select value={deliveryOid} onChange={(e) => setDeliveryOid(e.target.value)}>
              <option value="">— none —</option>
              {opt(lookups?.deliveryServices)}
            </select>
            <label>Tracking #<span className="srd-req">*</span></label>
            <input className={!trackingNo.trim() ? 'srd-empty' : ''} value={trackingNo} maxLength={100}
                   onChange={(e) => setTrackingNo(e.target.value)} />

            <label>Date Shipped</label>
            <UsDateInput className="srd-date" value={dateShipped} onChange={setDateShipped} />
            <label>Contact Phone</label>
            <input value={shipPhone} maxLength={100} onChange={(e) => setShipPhone(e.target.value)} />

            <label>Contact</label>
            <input value={shipContact} maxLength={100} onChange={(e) => setShipContact(e.target.value)} />
            <label>Email</label>
            <input value={shipEmail} maxLength={100} onChange={(e) => setShipEmail(e.target.value)} />
          </div>
        </section>

        {/* ── Test Info ── */}
        <section className="srd-sec">
          <h3>Test Info</h3>
          <div className="srd-grid">
            <label>Test For Subout</label>
            <textarea value={testForSubout} readOnly className="srd-ro" rows={2} />
            <label>Turn Around Time<span className="srd-req">*</span></label>
            <select className={tatOid ? '' : 'srd-empty'} value={tatOid} onChange={(e) => setTatOid(e.target.value)}>
              <option value="">— select —</option>
              {opt(lookups?.turnAroundTimes)}
            </select>

            <label>Number Of Samples</label>
            <input value={String(numberOfSamples)} readOnly className="srd-ro" />
            <label>Due Date</label>
            <input value={fmtLocalDateTime(dueDate)} readOnly className="srd-ro"
                   title="Computed from the Turn Around Time (working days / business hours)" />

            <label>Number Of Bottles</label>
            <input type="number" min={0} value={bottles} onChange={(e) => setBottles(e.target.value)} />
            <label>Remark</label>
            <textarea value={remark} maxLength={4000} rows={2} onChange={(e) => setRemark(e.target.value)} />

            <label>Preservative</label>
            <select value={preservativeOid} onChange={(e) => setPreservativeOid(e.target.value)}>
              <option value="">— none —</option>
              {opt(lookups?.preservatives)}
            </select>
            <label>Result Import EDD</label>
            <input value="N/A" readOnly className="srd-ro" />

            {!isEdit && (
              <>
                <span /><span />
                <label htmlFor="srd-scan-code">Scan Code</label>
                <input id="srd-scan-code" className="srd-scan" data-unsaved-ignore autoComplete="off"
                       value={scanText} maxLength={60} disabled={gridLoading || sampleRows.length === 0}
                       title="Scan a container label (JobID-SampleNo-Bottle, e.g. 261102058-001-A) to tick the samples that use it"
                       onChange={(e) => setScanText(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void scan(scanText); } }}
                       onBlur={() => { if (scanText.trim()) void scan(scanText); }} />
              </>
            )}
          </div>
        </section>

        {/* ── Bottom tabs ── */}
        <div className="srd-tabs" role="tablist">
          {tabs.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t}
                    className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
              {tabLabel(t)}
            </button>
          ))}
        </div>

        {tab === 'samples' && (
          <div className="srd-tabpane">
            {isEdit && canCreate && (
              <div className="srd-gridbar">
                <button type="button" className="ca-btn" onClick={() => setShowAdd(true)} disabled={busy}>
                  <i className="ti ti-plus" aria-hidden="true" /> Add
                </button>
                <button type="button" className="ca-btn" onClick={removeSamples} disabled={busy}>
                  <i className="ti ti-minus" aria-hidden="true" /> Remove
                </button>
              </div>
            )}
            <AppDataGrid<SuboutSampleDetailRow>
              rows={sampleRows}
              columns={sampleColumns}
              getRowId={keyOf}
              loading={gridLoading}
              selectable={isEdit ? canCreate : true}
              selectedIds={sampleSel}
              onSelectionChange={setSampleSel}
              maximizable
              exportFilename="subout-samples"
              emptyText="No samples."
            />
          </div>
        )}
        {tab === 'prices' && isEdit && (
          <div className="srd-tabpane">
            <SuboutTestPricesTab
              rows={prices}
              total={priceTotal}
              turnAroundTimes={lookups?.turnAroundTimes ?? []}
              readOnly={!canCreate}
              loading={pricesLoading}
              onEdit={editPrice}
            />
          </div>
        )}
        {(tab === 'attachments' || tab === 'notes') && !orderOid && (
          <div className="srd-tabpane srd-tab-stub">
            {tab === 'attachments'
              ? 'Save the subout order first, then add its attachments.'
              : 'Save the subout order first, then add its notes.'}
          </div>
        )}
        {/* Mounted for the whole edit view (hidden off its tabs) so the tab counts are live. */}
        {orderOid && (
          <div className="srd-tabpane" hidden={tab !== 'attachments' && tab !== 'notes'}>
            <PreLoginDocsPanel cocOid={orderOid} owner={TEST_ORDER_DOCS} embedded readOnly={!canCreate}
                               activeTab={tab === 'notes' ? 'note' : 'attachment'} onCounts={setDocCounts} />
          </div>
        )}
      </div>

      {actionBar}

      {showAdd && orderOid && (
        <SuboutAddSamplesDialog
          orderOid={orderOid}
          inGrid={sampleRows}
          removed={removed}
          onClose={() => setShowAdd(false)}
          onAdd={addSamples}
        />
      )}
    </div>
  );
}
